// Iframe-basiertes Paged.js-Rendering mit Diagnose-Channel.
//
// Strategie nach erstem Debug-Lauf:
//  - KEIN sandbox-Attribut (Inhalt ist von uns selbst, kein Risiko)
//  - document.open/write/close statt srcdoc (zuverlässiger über Browser hinweg)
//  - source-Check raus, dafür Prefix-Filter im postMessage
//  - sofortiges "alive"-Ping, damit wir sehen ob das Script überhaupt läuft
const PAGEDJS_CDN = "https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js";
/**
 * Liest die nach dem Render bekannte Heading→Seitenzahl-Map aus dem Iframe.
 * Setzt voraus, dass das Iframe-Script bereits geladen ist (also nach md2bp:rendered).
 */
export function extractBookmarks(iframe) {
    const fn = iframe.contentWindow?.md2bpExtractBookmarks;
    if (typeof fn !== "function")
        return [];
    try {
        return fn();
    }
    catch {
        return [];
    }
}
export function renderInIframe(iframe, html) {
    return new Promise((resolve) => {
        const log = [];
        const injected = `
<script>
  // Sofort melden, dass wir laufen
  try { window.parent.postMessage({type:"md2bp:log",level:"log",msg:"[iframe alive] origin=" + location.origin + " readyState=" + document.readyState}, "*"); } catch(e) {}

  // Console-Spiegelung
  (function(){
    function relay(type, args){
      try {
        window.parent.postMessage({type:"md2bp:log",level:type,msg:Array.from(args).map(function(a){
          try { return typeof a === "string" ? a : JSON.stringify(a); } catch(e) { return String(a); }
        }).join(" ")}, "*");
      } catch(e){}
    }
    ["log","warn","error"].forEach(function(t){
      var orig = console[t];
      console[t] = function(){ relay(t, arguments); orig.apply(console, arguments); };
    });
    window.addEventListener("error", function(ev){
      relay("error", ["[onerror]", ev.message, "in", ev.filename + ":" + ev.lineno]);
    });
    window.addEventListener("unhandledrejection", function(ev){
      relay("error", ["[unhandledrejection]", String(ev.reason && (ev.reason.message || ev.reason))]);
    });
  })();
</script>
<script src="${PAGEDJS_CDN}"
  onload="window.parent.postMessage({type:'md2bp:log',level:'log',msg:'[pagedjs CDN loaded]'},'*')"
  onerror="window.parent.postMessage({type:'md2bp:log',level:'error',msg:'[pagedjs CDN load FAILED]'},'*')"></script>
<style>
  /* Screen-Preview-Styling. !important damit Paged.js' interne Page-Styles
     nicht meine Border/Shadow überschreiben. */
  html, body { background: #c8c8c4 !important; }
  .pagedjs_pages {
    --md2bp-zoom: 1;
    gap: 14mm !important;
    padding: 14mm 0 !important;
    transform: scale(var(--md2bp-zoom));
    transform-origin: top center;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
  }
  /* Spread = Reading-Order-Pairs (P1+P2, P3+P4, …) — intuitiver in der Preview
     als bibliographisch-korrekte Vakat-Logik. */
  .pagedjs_pages[data-layout="spread"] {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    align-items: start !important;
    column-gap: 0 !important;
    row-gap: 14mm !important;
  }
  .pagedjs_pages[data-layout="spread"] .pagedjs_page:nth-child(odd)  { justify-self: end !important; }
  .pagedjs_pages[data-layout="spread"] .pagedjs_page:nth-child(even) { justify-self: start !important; }

  /* Klare Seiten-Abgrenzung: weißer Hintergrund, sichtbare Kante, Schatten */
  .pagedjs_page {
    background-color: white !important;
    background-image: none !important;
    border: 1px solid #888 !important;
    box-shadow: 0 8px 28px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.15) !important;
  }
  /* Bei Doppelseite: deutliche Mittellinie zwischen den paaren Seiten.
     NUR Bildschirm-Dekoration — @media screen, damit diese Linie NICHT in den
     Druck/X-3 durchschlägt. (Ihr Selektor hat höhere Spezifität als der
     @media-print-Border-Reset; ohne @media screen erschiene sonst auf geraden
     Seiten links eine graue Linie im PDF.) */
  @media screen {
    .pagedjs_pages[data-layout="spread"] .pagedjs_page:nth-child(even) {
      border-left: 2px solid #555 !important;
    }
  }

  /* Print-Reset */
  /* PRINT — alle Preview-Optik weg. 1:1-Mapping Paged.js-Page → Physische PDF-Seite. */
  @media print {
    html, body {
      background: white !important;
      margin: 0 !important;
      padding: 0 !important;
      min-height: 0 !important;
    }
    .pagedjs_pages,
    .pagedjs_pages[data-layout="spread"],
    .pagedjs_pages[data-layout="single"] {
      display: block !important;
      padding: 0 !important;
      margin: 0 !important;
      gap: 0 !important;
      row-gap: 0 !important;
      column-gap: 0 !important;
      grid-template-columns: none !important;
      flex-direction: initial !important;
      align-items: initial !important;
      transform: none !important;
      transform-origin: initial !important;
    }
    .pagedjs_page {
      background: white !important;
      border: 0 !important;
      box-shadow: none !important;
      margin: 0 !important;
      padding: 0 !important;
      display: block !important;
      /* Jede Paged.js-Page → eine physische PDF-Seite */
      page-break-after: always !important;
      break-after: page !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .pagedjs_page:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
  }
</style>
<script>
  // html-Element existiert IMMER, body ist erst nach head verfügbar — defensive Checks.
  document.documentElement.style.setProperty("background", "#c8c8c4", "important");

  // Aggressive Styling-Strategie: Body + Pages werden alle 250ms neu inline-gestylt.
  // Paged.js' eigene Inline-Styles können wir so dauerhaft überschreiben.
  var styleCallCount = 0;
  function md2bpStylePages() {
    styleCallCount++;
    try { document.documentElement.style.setProperty("background", "#c8c8c4", "important"); } catch(e) {}
    if (document.body) {
      try { document.body.style.setProperty("background", "#c8c8c4", "important"); } catch(e) {}
    }
    var pages = document.querySelectorAll(".pagedjs_page");
    pages.forEach(function(p) {
      try {
        p.style.setProperty("background-color", "white", "important");
        p.style.setProperty("border", "1px solid #888", "important");
        p.style.setProperty("box-shadow", "0 8px 28px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.15)", "important");
        p.style.setProperty("margin", "14mm auto", "important");
      } catch(e) {}
    });
    return pages.length;
  }

  // Bookmark-Extraktion für PDF-Outline-Injection:
  // liefert alle H1-H4 mit ihrer finalen Seitenzahl in der gerenderten Reihenfolge.
  // Cover-Inhalte und ToC-Title werden ausgeschlossen.
  window.md2bpExtractBookmarks = function() {
    var allPages = Array.from(document.querySelectorAll(".pagedjs_page"));
    var nodes = document.querySelectorAll("h1, h2, h3, h4");
    var out = [];
    nodes.forEach(function(h) {
      // Ausschlussfilter
      if (h.classList && (h.classList.contains("toc-title") || h.classList.contains("book-title") || h.classList.contains("book-subtitle"))) return;
      if (h.closest && (h.closest(".cover-page") || h.closest(".title-page"))) return;
      var page = h.closest(".pagedjs_page");
      if (!page) return;
      var pageNum = allPages.indexOf(page) + 1;
      if (pageNum <= 0) return;
      var title = (h.textContent || "").trim();
      if (!title) return;
      out.push({
        level: parseInt(h.tagName.charAt(1), 10),
        title: title,
        page: pageNum
      });
    });
    return out;
  };

  // ToC-Seitenzahlen per JS füllen — robuster als CSS-target-counter
  function md2bpFillToc() {
    var allPages = Array.from(document.querySelectorAll(".pagedjs_page"));
    var tocAnchors = document.querySelectorAll(".toc-list a[href^='#']");
    tocAnchors.forEach(function(a) {
      var href = a.getAttribute("href") || "";
      var id = href.slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      var page = target.closest(".pagedjs_page");
      if (!page) return;
      var pageNum = allPages.indexOf(page) + 1;
      if (pageNum > 0) {
        var span = a.querySelector(".toc-page");
        if (span) span.textContent = String(pageNum);
      }
    });
  }

  // Anfangs aggressiv neu setzen, danach abklingend.
  // Wichtig: während Print pausieren, sonst überschreibt das Interval unsere
  // Style-Resets und der gedruckte PDF zeigt wieder die Preview-Optik.
  var lastCount = -1;
  var stableTicks = 0;
  var renderedSent = false;
  var md2bpIsPrinting = false;
  var iv = setInterval(function() {
    if (md2bpIsPrinting) return;  // während Print keine Style-Reapplikation
    var n = md2bpStylePages();
    if (n > 0 && n === lastCount) {
      stableTicks++;
      if (!renderedSent && stableTicks >= 4) {
        renderedSent = true;
        md2bpFillToc();
        // Diagnose: was steht auf den ersten 10 Seiten?
        try {
          var pages = document.querySelectorAll(".pagedjs_page");
          var dump = [];
          for (var i = 0; i < Math.min(pages.length, 10); i++) {
            var p = pages[i];
            var first = p.querySelector("h1, h2, h3, .toc-title, .book-title, .cover-page img");
            var label = "(leer)";
            if (first) {
              if (first.tagName === "IMG") label = "[Cover-Image]";
              else label = (first.textContent || "").trim().slice(0, 50);
            }
            dump.push("p" + (i+1) + ": " + label);
          }
          fetch("/__debug?kind=pages", {
            method: "POST", headers: { "Content-Type": "text/plain" },
            body: "BUILD=" + (window.location.search || "?") + "\\n" + dump.join("\\n")
          });
        } catch(e) {}
        window.parent.postMessage({type:"md2bp:log",level:"log",msg:"[stable] pages=" + n + " for 1s, sending md2bp:rendered + filled ToC"}, "*");
        window.parent.postMessage({type:"md2bp:rendered", pages: n}, "*");
      }
    } else {
      stableTicks = 0;
      lastCount = n;
    }
    if (styleCallCount % 10 === 0) {
      window.parent.postMessage({type:"md2bp:log",level:"log",msg:"[style-apply #" + styleCallCount + "] pages=" + n}, "*");
    }
  }, 250);
  // Nach 30 s aufhören
  setTimeout(function(){ clearInterval(iv); }, 30000);

  window.addEventListener("pagedjs:rendered", function() {
    md2bpStylePages();
    md2bpFillToc();
    setTimeout(function(){ md2bpStylePages(); md2bpFillToc(); }, 100);
    setTimeout(function(){ md2bpStylePages(); md2bpFillToc(); }, 500);
    var pages = document.querySelectorAll(".pagedjs_page").length;
    window.parent.postMessage({type:"md2bp:log",level:"log",msg:"[pagedjs:rendered] fired"}, "*");
    if (!renderedSent) {
      renderedSent = true;
      window.parent.postMessage({type:"md2bp:rendered", pages: pages}, "*");
    }
  });

  // Vor dem Drucken ALLE Inline-Preview-Styles entfernen — sonst gewinnen
  // sie als !important-Inline über @media print. State zwischenspeichern damit
  // wir nach dem Druck Preview wieder herstellen.
  var md2bpSavedContainerStyle = "";
  var md2bpSavedBodyMinHeight = "";
  function md2bpResetPrintStyles() {
    md2bpIsPrinting = true;
    try {
      document.documentElement.style.removeProperty("background");
      if (document.body) {
        document.body.style.removeProperty("background");
        md2bpSavedBodyMinHeight = document.body.style.minHeight || "";
        document.body.style.removeProperty("min-height");
        document.body.style.removeProperty("margin");
        document.body.style.removeProperty("padding");
      }
      var container = document.querySelector(".pagedjs_pages");
      if (container) {
        md2bpSavedContainerStyle = container.getAttribute("style") || "";
        // Inline-styles komplett entfernen damit @media print greift
        container.removeAttribute("style");
      }
      document.querySelectorAll(".pagedjs_page").forEach(function(p) {
        p.style.removeProperty("background-color");
        p.style.removeProperty("border");
        p.style.removeProperty("box-shadow");
        p.style.removeProperty("margin");
        p.style.removeProperty("display");  // sichtbar machen für Print, falls paged-mode display:none gesetzt hatte
      });
    } catch(e) {}
  }
  function md2bpRestorePreviewStyles() {
    md2bpIsPrinting = false;
    try {
      var container = document.querySelector(".pagedjs_pages");
      if (container && md2bpSavedContainerStyle) {
        container.setAttribute("style", md2bpSavedContainerStyle);
      }
      if (document.body && md2bpSavedBodyMinHeight) {
        document.body.style.minHeight = md2bpSavedBodyMinHeight;
      }
      md2bpStylePages();
      md2bpFillToc();
    } catch(e) {}
  }
  window.addEventListener("beforeprint", md2bpResetPrintStyles);
  window.addEventListener("afterprint", md2bpRestorePreviewStyles);
  // Diagnose nach 4s und 12s — Margin-Boxes auf Seite 5 (mid-book) statt 1 (Titelseite)
  setTimeout(function(){
    var p = document.querySelectorAll(".pagedjs_page").length;
    var imgs = document.querySelectorAll("img");
    var imgInfo = Array.prototype.map.call(imgs, function(i){
      return "[" + (i.complete ? (i.naturalWidth+"x"+i.naturalHeight) : "loading") + " " + (i.src||"").slice(-40) + "]";
    }).join(" ");
    window.parent.postMessage({type:"md2bp:log",level:"log",msg:"[4s] pages="+p+" h1="+document.querySelectorAll("h1").length+" imgs="+imgs.length+" "+imgInfo},"*");
  }, 4000);
  setTimeout(function(){
    var pages = document.querySelectorAll(".pagedjs_page");
    var s5 = pages[Math.min(4, pages.length - 1)];
    // Margin-Box-Texte holen — Paged.js packt sie in ein .pagedjs_margin-content-Kind
    function boxText(p, sel) {
      var outer = p && p.querySelector(sel);
      if (!outer) return "--";
      var inner = outer.querySelector(".pagedjs_margin-content");
      var txt = inner ? inner.textContent : outer.textContent;
      return (txt || "").trim() || "(leer)";
    }
    if (s5) {
      var diagText = "[p5-text] TL=" + JSON.stringify(boxText(s5, ".pagedjs_margin-top-left")) +
        " TC=" + JSON.stringify(boxText(s5, ".pagedjs_margin-top-center")) +
        " TR=" + JSON.stringify(boxText(s5, ".pagedjs_margin-top-right")) +
        " BC=" + JSON.stringify(boxText(s5, ".pagedjs_margin-bottom-center"));
      window.parent.postMessage({type:"md2bp:log",level:"log",msg:diagText}, "*");
      // Auch direkt an den Server posten, weil parent das logfile schon geschrieben hat
      try {
        fetch("/__debug?kind=diag", {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: diagText
        });
      } catch(e) {}
    }

    // Computed-Style-Diagnose: was hat Paged.js am Ende durchgesetzt?
    if (s5) {
      var cs = getComputedStyle(s5);
      var pagesContainer = document.querySelector(".pagedjs_pages");
      var pcs = pagesContainer ? getComputedStyle(pagesContainer) : null;
      var bs = document.body ? getComputedStyle(document.body) : null;
      var computedMsg = "[computed] page.bg='" + cs.backgroundColor + "' page.border='" + cs.borderTopWidth + " " + cs.borderTopStyle + " " + cs.borderTopColor +
        "' page.shadow='" + cs.boxShadow.slice(0,80) +
        "' container.display='" + (pcs ? pcs.display : "--") +
        "' container.transform='" + (pcs ? pcs.transform.slice(0,40) : "--") +
        "' body.bg='" + (bs ? bs.backgroundColor : "--") +
        "' iframe.size=" + window.innerWidth + "x" + window.innerHeight +
        " page.size=" + s5.offsetWidth + "x" + s5.offsetHeight;
      window.parent.postMessage({type:"md2bp:log",level:"log",msg:computedMsg},"*");
      try {
        fetch("/__debug?kind=computed", {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: computedMsg
        });
      } catch(e) {}
    }
  }, 12000);
</script>
`;
        const enriched = html.replace("</head>", injected + "</head>");
        const onMessage = (ev) => {
            const d = ev.data;
            if (!d || typeof d !== "object" || typeof d.type !== "string")
                return;
            if (!d.type.startsWith("md2bp:"))
                return;
            if (d.type === "md2bp:log") {
                log.push(`[${d.level}] ${d.msg}`);
                return;
            }
            if (d.type === "md2bp:rendered") {
                window.removeEventListener("message", onMessage);
                resolve({ pageCount: d.pages, log });
            }
        };
        window.addEventListener("message", onMessage);
        // Variante 1: document.open/write/close — zuverlässiger als srcdoc bei manchen Browsern
        const doc = iframe.contentDocument;
        if (!doc) {
            window.removeEventListener("message", onMessage);
            resolve({ pageCount: 0, log, error: "iframe.contentDocument war null" });
            return;
        }
        try {
            doc.open();
            doc.write(enriched);
            doc.close();
        }
        catch (e) {
            window.removeEventListener("message", onMessage);
            resolve({ pageCount: 0, log, error: "doc.write warf: " + String(e.message || e) });
            return;
        }
        setTimeout(() => {
            window.removeEventListener("message", onMessage);
            const p = iframe.contentDocument?.querySelectorAll(".pagedjs_page").length ?? 0;
            resolve({
                pageCount: p,
                log,
                error: p > 0
                    ? undefined
                    : "Timeout (30s) ohne pagedjs:rendered. Letzte Logs: " + log.slice(-12).join(" | "),
            });
        }, 30000);
    });
}
/**
 * Print-Dialog des Iframes öffnen, mit korrekter @page-Größe.
 * Paged.js' Polyfill konsumiert die @page-Rules aus dem Stylesheet und entfernt
 * sie — daher injizieren wir vor dem Druck eine frische @page-Rule, die der
 * Browser fürs PDF nutzt.
 */
export function printIframe(iframe, opts) {
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument;
    if (!win || !doc)
        return;
    // Physische PDF-Seitengröße bestimmen.
    //
    // WICHTIG: Bei aktivem Beschnitt/Schnittmarken rendert Paged.js jedes Sheet
    // GRÖSSER als die Trim-Größe (Trim + 2×Bleed + Platz für die Schnittmarken).
    // Wenn wir die physische @page nur auf die Trim-Größe setzen, passt die
    // gedruckte Seite nicht zum gerenderten Sheet → asymmetrische weiße Ränder
    // (unten/rechts) und „falsche" Seitengröße. Wir messen deshalb das tatsächlich
    // gerenderte Sheet und setzen @page exakt darauf. Ohne Bleed/Marks ist das
    // Sheet = Trim, also kein Unterschied zum bisherigen Verhalten.
    //
    // clientWidth/Height (statt offsetWidth) lässt unsere 1px-Preview-Border außen
    // vor und ist unabhängig vom Vorschau-Zoom (CSS transform: scale beeinflusst
    // Layout-Maße nicht).
    let wMm = opts.widthMm;
    let hMm = opts.heightMm;
    const PX_PER_MM = 96 / 25.4; // CSS-px sind per Definition 1/96 inch
    const sheet = doc.querySelector(".pagedjs_sheet") ??
        doc.querySelector(".pagedjs_page");
    if (sheet && sheet.clientWidth > 0 && sheet.clientHeight > 0) {
        const measuredW = sheet.clientWidth / PX_PER_MM;
        const measuredH = sheet.clientHeight / PX_PER_MM;
        // Nur übernehmen, wenn das Sheet das Trim spürbar überschreitet (Bleed/Marks
        // aktiv). Sonst die exakten Trim-Werte behalten — sonst würde die px→mm-
        // Rundung die saubere Trim-Größe um ~0.1mm verkleinern.
        if (measuredW - opts.widthMm > 0.5 || measuredH - opts.heightMm > 0.5) {
            wMm = measuredW;
            hMm = measuredH;
        }
    }
    // Wenn schon ein print-page-style existiert, ersetzen
    let style = doc.getElementById("md2bp-print-page");
    if (!style) {
        style = doc.createElement("style");
        style.id = "md2bp-print-page";
        doc.head.appendChild(style);
    }
    style.textContent = `
    @media print {
      @page {
        size: ${wMm.toFixed(2)}mm ${hMm.toFixed(2)}mm;
        margin: 0;
      }
    }
  `;
    win.focus();
    win.print();
}
